import { useStaticQuery, graphql } from 'gatsby'
import { useEffect, useRef } from 'react'
import { GatsbyImage } from 'gatsby-plugin-image'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { DrawSVGPlugin } from 'gsap/DrawSVGPlugin'
import { gsap, Elastic } from 'gsap'
import { SplitText } from 'gsap/SplitText'

import style from './SubHero.module.css'

const SubHero = () => {
  const notifier = useRef()

  const data = useStaticQuery(graphql`
    {
      AWSDivider: file(relativePath: { eq: "services/managed/AWS-divider.png" }) {
        childImageSharp {
          gatsbyImageData(width: 986, placeholder: TRACED_SVG, layout: FIXED)
        }
      }
    }
  `)

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger)
    gsap.registerPlugin(DrawSVGPlugin)
    gsap.registerPlugin(SplitText)

    const statText1 = new SplitText('.splittext1', { type: 'words,chars' }).chars
    const statText2 = new SplitText('.splittext2', { type: 'words,chars' }).chars
    const statText3 = new SplitText('.splittext3', { type: 'words,chars' }).chars
    const tlStats = gsap.timeline({
      scrollTrigger: {
        trigger: `.${style.AWSDivider}`,
        start: 'top 70%',
      },
    })
    gsap.set('.alerttext', { x: 40, y: '+=25' })
    tlStats.set('.points99, .points62, .points65', {
      rotate: 175,
      x: '+=0',
      transformOrigin: '50% 50%',
    })
    tlStats.from(statText1, { opacity: 0, x: -5, stagger: 0.025, duration: 0.05 }, 0)
    tlStats.from(statText2, { opacity: 0, x: -5, stagger: 0.025, duration: 0.05 }, 0)
    tlStats.from(statText3, { opacity: 0, x: -5, stagger: 0.025, duration: 0.05 }, 0)
    tlStats.from(`.${style.source}`, { opacity: 0, x: -5, duration: 1 }, 1.5)

    tlStats.fromTo(
      '.points99',
      { drawSVG: '0%' },
      { drawSVG: '70%', duration: 1, ease: 'power1.easeInOut' },
      0
    )
    tlStats.fromTo(
      '.baritem',
      { y: '+=20', opacity: 0 },
      { y: 0, opacity: 1, duration: 1, ease: 'power1.easeInOut', stagger: 0.1 },
      0
    )
    tlStats.fromTo(
      '.points65',
      { drawSVG: '0%' },
      { drawSVG: '65%', duration: 1, ease: 'power1.easeInOut' },
      0.25
    )
    tlStats.fromTo(
      '.points62',
      { drawSVG: '0%' },
      { drawSVG: '43%', duration: 1, ease: 'power1.easeInOut' },
      0.5
    )
    tlStats.fromTo(
      '.alertbox, .alerticon',
      { opacity: 0, y: '+=55' },
      {
        opacity: 1,
        duration: 1.8,
        y: 25,
        stagger: 0.2,
        ease: Elastic.easeOut.config(1, 0.8),
      },
      3
    )
    tlStats.from('.alerttext', { opacity: 0 }, 3.5)

    return () => {
      tlStats.kill()
    }
  }, [])
  return (
    <div className={style.outerWrapper}>
      <GatsbyImage
        image={data.AWSDivider.childImageSharp.gatsbyImageData}
        alt=""
        className={style.AWSDivider}
      />
      <div className={style.wrapper}>
        <svg className={style.graphbars} viewBox="0 0 1450 212">
          <defs>
            <linearGradient
              x1="49.3373453%"
              y1="90.5184659%"
              x2="50.9585799%"
              y2="-20.2429199%"
              id="agraph"
            >
              <stop stopColor="#FFC42A" offset="0%" />
              <stop stopColor="#FF0061" stopOpacity={0.946473053} offset="100%" />
            </linearGradient>
            <linearGradient
              x1="48.1903589%"
              y1="90.5184659%"
              x2="52.6177822%"
              y2="-20.2429199%"
              id="bgraph"
            >
              <stop stopColor="#FFC42A" offset="0%" />
              <stop stopColor="#FF0061" stopOpacity={0.946473053} offset="100%" />
            </linearGradient>
            <linearGradient x1="48.8801136%" y1="90.5184659%" x2="51.62%" y2="-20.2429199%" id="c">
              <stop stopColor="#FFC42A" offset="0%" />
              <stop stopColor="#FF0061" stopOpacity={0.946473053} offset="100%" />
            </linearGradient>
            <linearGradient
              x1="48.4377554%"
              y1="90.5184659%"
              x2="52.2599045%"
              y2="-20.2429199%"
              id="dgraph"
            >
              <stop stopColor="#FFC42A" offset="0%" />
              <stop stopColor="#FF0061" stopOpacity={0.946473053} offset="100%" />
            </linearGradient>
            <linearGradient
              x1="47.321985%"
              y1="90.5184659%"
              x2="53.8739505%"
              y2="-20.2429199%"
              id="egraph"
            >
              <stop stopColor="#FFC42A" offset="0%" />
              <stop stopColor="#FF0061" stopOpacity={0.946473053} offset="100%" />
            </linearGradient>
            <linearGradient
              x1="49.4393591%"
              y1="90.5184659%"
              x2="50.8110093%"
              y2="-20.2429199%"
              id="fgraph"
            >
              <stop stopColor="#FFC42A" offset="0%" />
              <stop stopColor="#FF0061" stopOpacity={0.946473053} offset="100%" />
            </linearGradient>
            <linearGradient
              x1="47.7574365%"
              y1="90.5184659%"
              x2="53.244037%"
              y2="-20.2429199%"
              id="ggraph"
            >
              <stop stopColor="#FFC42A" offset="0%" />
              <stop stopColor="#FF0061" stopOpacity={0.946473053} offset="100%" />
            </linearGradient>
            <linearGradient
              x1="49.0631528%"
              y1="90.5184659%"
              x2="51.3552201%"
              y2="-20.2429199%"
              id="hgraph"
            >
              <stop stopColor="#FFC42A" offset="0%" />
              <stop stopColor="#FF0061" stopOpacity={0.946473053} offset="100%" />
            </linearGradient>
            <linearGradient
              x1="45.5204545%"
              y1="90.5184659%"
              x2="56.48%"
              y2="-20.2429199%"
              id="igraph"
            >
              <stop stopColor="#FFC42A" offset="0%" />
              <stop stopColor="#FF0061" stopOpacity={0.946473053} offset="100%" />
            </linearGradient>
            <linearGradient
              x1="41.3589015%"
              y1="90.5184659%"
              x2="62.5%"
              y2="-20.2429199%"
              id="jgraph"
            >
              <stop stopColor="#FFC42A" offset="0%" />
              <stop stopColor="#FF0061" stopOpacity={0.946473053} offset="100%" />
            </linearGradient>
            <linearGradient
              x1="43.8482805%"
              y1="90.5184659%"
              x2="58.8989258%"
              y2="-20.2429199%"
              id="kgraph"
            >
              <stop stopColor="#FFC42A" offset="0%" />
              <stop stopColor="#FF0061" stopOpacity={0.946473053} offset="100%" />
            </linearGradient>
            <linearGradient
              x1="25.3931219%"
              y1="90.5184659%"
              x2="85.5957031%"
              y2="-20.2429199%"
              id="lgraph"
            >
              <stop stopColor="#FFC42A" offset="0%" />
              <stop stopColor="#FF0061" stopOpacity={0.946473053} offset="100%" />
            </linearGradient>
          </defs>
          <g stroke="none" strokeWidth={1} fill="none" fillRule="evenodd" opacity={0.25}>
            <path className="baritem" fill="url(#agraph)" d="M0 17H27V212H0z" />
            <path className="baritem" fill="url(#bgraph)" d="M35 94H62V212H35z" />
            <path className="baritem" fill="url(#cgraph)" d="M70 62H97V212H70z" />
            <path className="baritem" fill="url(#dgraph)" d="M104 85H131V212H104z" />
            <path className="baritem" fill="url(#egraph)" d="M139 115H166V212H139z" />
            <path className="baritem" fill="url(#fgraph)" d="M174 0H201V212H174z" />
            <path className="baritem" fill="url(#ggraph)" d="M209 106H236V212H209z" />
            <path className="baritem" fill="url(#hgraph)" d="M243 48H270V212H243z" />
            <path className="baritem" fill="url(#igraph)" d="M278 137H305V212H278z" />
            <path className="baritem" fill="url(#dgraph)" d="M313 85H340V212H313z" />
            <path className="baritem" fill="url(#agraph)" d="M348 17H375V212H348z" />
            <path className="baritem" fill="url(#igraph)" d="M382 137H409V212H382z" />
            <path className="baritem" fill="url(#jgraph)" d="M417 158H444V212H417z" />
            <path className="baritem" fill="url(#egraph)" d="M452 115H479V212H452z" />
            <path className="baritem" fill="url(#kgraph)" d="M487 148H514V212H487z" />
            <path className="baritem" fill="url(#agraph)" d="M522 17H549V212H522z" />
            <path className="baritem" fill="url(#lgraph)" d="M557 180H584V212H557z" />
            <path className="baritem" fill="url(#dgraph)" d="M591 85H618V212H591z" />
            <path className="baritem" fill="url(#egraph)" d="M626 115H653V212H626z" />
            <path className="baritem" fill="url(#fgraph)" d="M661 0H688V212H661z" />
            <path className="baritem" fill="url(#ggraph)" d="M696 106H723V212H696z" />
            <path className="baritem" fill="url(#hgraph)" d="M730 48H757V212H730z" />
            <path className="baritem" fill="url(#igraph)" d="M765 137H792V212H765z" />
            <path className="baritem" fill="url(#dgraph)" d="M800 85H827V212H800z" />
            <path
              className="baritem notification"
              ref={notifier}
              fill="url(#agraph)"
              d="M835 17H862V212H835z"
            />
            <path className="baritem" fill="url(#igraph)" d="M869 137H896V212H869z" />
            <path className="baritem" fill="url(#jgraph)" d="M904 158H931V212H904z" />
            <path className="baritem" fill="url(#egraph)" d="M939 115H966V212H939z" />
            <path className="baritem" fill="url(#lgraph)" d="M971 180H998V212H971z" />
            <path className="baritem" fill="url(#dgraph)" d="M1005 85H1032V212H1005z" />
            <path className="baritem" fill="url(#egraph)" d="M1040 115H1067V212H1040z" />
            <path className="baritem" fill="url(#fgraph)" d="M1075 0H1102V212H1075z" />
            <path className="baritem" fill="url(#ggraph)" d="M1110 106H1137V212H1110z" />
            <path className="baritem" fill="url(#hgraph)" d="M1144 48H1171V212H1144z" />
            <path className="baritem" fill="url(#igraph)" d="M1179 137H1206V212H1179z" />
            <path className="baritem" fill="url(#dgraph)" d="M1214 85H1241V212H1214z" />
            <path className="baritem" fill="url(#agraph)" d="M1249 17H1276V212H1249z" />
            <path className="baritem" fill="url(#igraph)" d="M1283 137H1310V212H1283z" />
            <path className="baritem" fill="url(#jgraph)" d="M1318 158H1345V212H1318z" />
            <path className="baritem" fill="url(#egraph)" d="M1353 115H1380V212H1353z" />
            <path className="baritem" fill="url(#lgraph)" d="M1389 180H1416V212H1389z" />
            <path className="baritem" fill="url(#dgraph)" d="M1423 85H1450V212H1423z" />
          </g>
        </svg>
        <div className={style.container}>
          <div className={style.item}>
            <svg width="133px" height="132px" viewBox="0 0 115 132">
              <defs>
                <linearGradient
                  x1="15.4356061%"
                  y1="90.5184659%"
                  x2="114.318971%"
                  y2="21.4133523%"
                  id="ms-percent-99-a"
                >
                  <stop stopColor="#FFC42A" offset="0%" />
                  <stop stopColor="#FF0061" stopOpacity={0.946473053} offset="100%" />
                </linearGradient>
                <filter
                  x="-19.0%"
                  y="-19.0%"
                  width="138.0%"
                  height="138.0%"
                  filterUnits="objectBoundingBox"
                  id="ms-percent-99-b"
                >
                  <feGaussianBlur stdDeviation={5} in="SourceGraphic" />
                </filter>
              </defs>
              <g stroke="none" strokeWidth={1} fill="none" fillRule="evenodd">
                <path
                  d="M25 93.301c13.807 23.915 44.387 32.109 68.301 18.302 23.915-13.808 32.109-44.387 18.302-68.302C97.795 19.387 67.216 11.193 43.3 25 19.387 38.807 11.193 69.387 25 93.301z"
                  stroke="#FFF"
                  strokeWidth={20}
                  opacity={0.0926283892}
                  strokeDasharray="2,2"
                  transform="translate(-60 -959) translate(58.699 956.699)"
                />
                <path
                  className="points99"
                  d="M68.301 118.301c27.615 0 50-22.385 50-50 0-27.614-22.385-50-50-50-27.614 0-50 22.386-50 50 0 27.615 22.386 50 50 50z"
                  stroke="url(#ms-percent-99-a)"
                  strokeWidth={8}
                  opacity={0.650321691}
                  strokeLinecap="round"
                  strokeDasharray="198,20"
                  filter="url(#ms-percent-99-b)"
                  transform="translate(-60 -959) translate(58.699 956.699) rotate(60 68.301 68.301)"
                />
                <path
                  className="points99"
                  d="M68.301 118.301c27.615 0 50-22.385 50-50 0-27.614-22.385-50-50-50-27.614 0-50 22.386-50 50 0 27.615 22.386 50 50 50z"
                  stroke="url(#ms-percent-99-a)"
                  strokeWidth={8}
                  strokeLinecap="round"
                  strokeDasharray="198,20"
                  transform="translate(-60 -959) translate(58.699 956.699) rotate(60 68.301 68.301)"
                />
                <text
                  fontFamily="GeomanistUltra"
                  fontSize={30}
                  fontWeight="normal"
                  fill="#FFF"
                  transform="translate(-60 -959) translate(58.699 956.699)"
                >
                  <tspan x={41.2792975} y={79.3012702}>
                    70%
                  </tspan>
                </text>
              </g>
            </svg>
            <p className="splittext1">
              70% of breaches were perpetrated by external actors, the majority being motivated by
              financial gain.
            </p>
          </div>
          <div className={style.item}>
            <svg width="138px" height="155px" viewBox="0 0 115 155">
              <defs>
                <linearGradient
                  x1="15.4356061%"
                  y1="90.5184659%"
                  x2="114.318971%"
                  y2="21.4133523%"
                  id="ms-percent-65-a"
                >
                  <stop stopColor="#FFC42A" offset="0%" />
                  <stop stopColor="#FF0061" stopOpacity={0.946473053} offset="100%" />
                </linearGradient>
                <filter
                  x="-34.0%"
                  y="-34.0%"
                  width="168.0%"
                  height="168.0%"
                  filterUnits="objectBoundingBox"
                  id="ms-percent-65-b"
                >
                  <feGaussianBlur stdDeviation={10} in="SourceGraphic" />
                </filter>
              </defs>
              <g stroke="none" strokeWidth={1} fill="none" fillRule="evenodd">
                <path
                  d="M27.313 95.614c13.807 23.914 44.386 32.108 68.3 18.301 23.915-13.807 32.11-44.387 18.302-68.301-13.807-23.915-44.387-32.109-68.301-18.301-23.915 13.807-32.109 44.386-18.301 68.3z"
                  stroke="#FFF"
                  strokeWidth={20}
                  opacity={0.0926283892}
                  strokeDasharray="2,2"
                  transform="translate(-772 -950) translate(761.386 956.386)"
                />
                <path
                  d="M70.614 120.614c27.614 0 50-22.386 50-50s-22.386-50-50-50-50 22.386-50 50 22.386 50 50 50z"
                  stroke="url(#ms-percent-65-a)"
                  strokeWidth={8}
                  strokeLinecap="round"
                  className="points65"
                  strokeDasharray="190,150"
                  transform="translate(-772 -950) translate(761.386 956.386) rotate(42 70.614 70.614)"
                />
                <text
                  fontFamily="GeomanistUltra"
                  fontSize={30}
                  fontWeight="normal"
                  fill="#FFF"
                  transform="translate(-772 -950) translate(761.386 956.386)"
                >
                  <tspan x={43.5917989} y={81.6137716}>
                    65%
                  </tspan>
                </text>
                <path
                  d="M70.614 120.614c27.614 0 50-22.386 50-50s-22.386-50-50-50-50 22.386-50 50 22.386 50 50 50z"
                  stroke="url(#ms-percent-65-a)"
                  strokeWidth={8}
                  opacity={0.65}
                  className="points65"
                  strokeLinecap="round"
                  strokeDasharray="190,150"
                  filter="url(#ms-percent-65-b)"
                  transform="translate(-772 -950) translate(761.386 956.386) rotate(42 70.614 70.614)"
                />
              </g>
            </svg>
            <p className="splittext2">
              Two in three (65%) respondents consider cloud security the most important skill for
              migrating or implementing cloud infrastructure
            </p>
          </div>
          <div className={style.item}>
            <svg width="138px" height="155px" viewBox="0 0 115 155">
              <defs>
                <linearGradient
                  x1="15.4356061%"
                  y1="90.5184659%"
                  x2="114.318971%"
                  y2="21.4133523%"
                  id="ms-percent-62-a"
                >
                  <stop stopColor="#FFC42A" offset="0%" />
                  <stop stopColor="#FF0061" stopOpacity={0.946473053} offset="100%" />
                </linearGradient>
                <filter
                  x="-34.0%"
                  y="-34.0%"
                  width="168.0%"
                  height="168.0%"
                  filterUnits="objectBoundingBox"
                  id="ms-percent-62-b"
                >
                  <feGaussianBlur stdDeviation={10} in="SourceGraphic" />
                </filter>
              </defs>
              <g stroke="none" strokeWidth={1} fill="none" fillRule="evenodd">
                <path
                  d="M27.313 95.614c13.807 23.914 44.386 32.108 68.3 18.301 23.915-13.807 32.11-44.387 18.302-68.301-13.807-23.915-44.387-32.109-68.301-18.301-23.915 13.807-32.109 44.386-18.301 68.3z"
                  stroke="#FFF"
                  strokeWidth={20}
                  opacity={0.0926283892}
                  strokeDasharray="2,2"
                  transform="translate(-68 -1108) translate(57.386 1114.386)"
                />
                <path
                  d="M70.614 120.614c27.614 0 50-22.386 50-50s-22.386-50-50-50-50 22.386-50 50 22.386 50 50 50z"
                  stroke="url(#ms-percent-62-a)"
                  strokeWidth={8}
                  strokeLinecap="round"
                  strokeDasharray="190,150"
                  className="points62"
                  transform="translate(-68 -1108) translate(57.386 1114.386) rotate(42 70.614 70.614)"
                />
                <text
                  fontFamily="GeomanistUltra"
                  fontSize={30}
                  fontWeight="900"
                  fill="#FFF"
                  transform="translate(-68 -1108) translate(57.386 1114.386)"
                >
                  <tspan x={43.5917989} y={81.6137716}>
                    43%
                  </tspan>
                </text>
                <path
                  d="M70.614 120.614c27.614 0 50-22.386 50-50s-22.386-50-50-50-50 22.386-50 50 22.386 50 50 50z"
                  stroke="url(#ms-percent-62-a)"
                  strokeWidth={8}
                  opacity={0.65}
                  strokeLinecap="round"
                  className="points62"
                  strokeDasharray="190,150"
                  filter="url(#ms-percent-62-b)"
                  transform="translate(-68 -1108) translate(57.386 1114.386) rotate(42 70.614 70.614)"
                />
              </g>
            </svg>
            <p className="splittext3">Web application servers were targeted in 43% of attacks.</p>
            <br />
            <br />
            <div className={style.source}>
              <a
                href="https://enterprise.verizon.com/resources/reports/2020-data-breach-investigations-report.pdf"
                target="_blank"
                rel="noreferrer"
              >
                Source: 2020 Data Breach Investigations Report
              </a>
            </div>
          </div>
          <svg width="284px" height="174px" viewBox="0 0 284 174" className={style.criticalAlert}>
            <defs>
              <path
                d="M4 0h228a4 4 0 014 4v112a4 4 0 01-4 4H122.377l-4.917 10-5.074-10H4a4 4 0 01-4-4V4a4 4 0 014-4z"
                id="ms-critical-alert-b"
              />
              <filter
                x="-16.1%"
                y="-23.1%"
                width="135.6%"
                height="164.6%"
                filterUnits="objectBoundingBox"
                id="ms-critical-alert-a"
              >
                <feOffset dx={4} dy={12} in="SourceAlpha" result="shadowOffsetOuter1" />
                <feGaussianBlur
                  stdDeviation={12}
                  in="shadowOffsetOuter1"
                  result="shadowBlurOuter1"
                />
                <feColorMatrix
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.104261705 0"
                  in="shadowBlurOuter1"
                />
              </filter>
              <linearGradient
                x1="15.4356061%"
                y1="50.1425887%"
                x2="114.318971%"
                y2="49.8994006%"
                id="ms-critical-alert-c"
              >
                <stop stopColor="#FFC42A" offset="0%" />
                <stop stopColor="#FF0061" stopOpacity={0.946473053} offset="100%" />
              </linearGradient>
              <linearGradient
                x1="15.4356061%"
                y1="75.3943858%"
                x2="114.318971%"
                y2="32.0837156%"
                id="aalert"
              >
                <stop stopColor="#FFC42A" offset="0%" />
                <stop stopColor="#FF0061" stopOpacity={0.946473053} offset="100%" />
              </linearGradient>
              <path
                className="alerticon"
                d="M26.536 4.016l18.557 29.382A3 3 0 0142.557 38H5.443a3 3 0 01-2.536-4.602L21.464 4.016a3 3 0 015.072 0z"
                id="balert"
              />
            </defs>

            <g stroke="none" strokeWidth={1} fill="none" fillRule="evenodd" className="alertbox">
              <g transform="translate(-954 -1102) translate(974 1114)">
                <use
                  fill="#000"
                  filter="url(#ms-critical-alert-a)"
                  xlinkHref="#ms-critical-alert-b"
                />
                <use fill="#0D2435" xlinkHref="#ms-critical-alert-b" />
              </g>
              <path
                d="M0 116h236a4 4 0 01-4 4H122.377l-4.917 10-5.074-10H4a4 4 0 01-4-4z"
                fill="url(#ms-critical-alert-c)"
                transform="translate(-954 -1102) translate(974 1114)"
              />
            </g>
            <g stroke="none" strokeWidth={1} fill="none" fillRule="evenodd">
              <g transform="translate(40 25)">
                <use fill="url(#aalert)" xlinkHref="#balert" />
                <use stroke="#FFF" fillOpacity={0.754370629} fill="#080808" xlinkHref="#balert" />
              </g>
              <path
                className="alerttext"
                d="M25.664 27.488L25.88 15.2h-2.736l.192 12.288h2.328zm-1.152 4.824c.936 0 1.704-.768 1.704-1.68a1.71 1.71 0 00-1.704-1.704 1.71 1.71 0 00-1.704 1.704c0 .912.768 1.68 1.704 1.68zM63.059 29c.707 0 1.506-.171 2.398-.513V27.04c-.83.49-1.65.735-2.46.735-.61 0-1.166-.21-1.668-.628-.502-.419-.915-1.05-1.238-1.892-.323-.842-.484-1.76-.484-2.756 0-.98.156-1.886.469-2.72.312-.835.724-1.469 1.234-1.9.51-.432 1.072-.648 1.687-.648.815 0 1.635.245 2.46.735v-1.454c-.892-.342-1.691-.513-2.398-.513-.943 0-1.807.26-2.591.782-.784.521-1.392 1.287-1.822 2.298-.43 1.011-.646 2.151-.646 3.42 0 1.28.218 2.426.653 3.44.436 1.013 1.046 1.777 1.83 2.29.784.513 1.643.77 2.576.77zm6.18-.371v-5.205h1.553l3.16 5.205h1.845l-3.452-5.726c.595-.337 1.05-.694 1.365-1.07.315-.377.562-.805.742-1.284.18-.479.269-.955.269-1.43 0-.51-.135-.987-.404-1.429a2.477 2.477 0 00-1.141-.991c-.492-.219-1.259-.328-2.299-.328h-3.19V28.63h1.553zm2.176-6.437H69.24v-4.589h1.16c1.072 0 1.81.17 2.218.51.408.34.612.796.612 1.37 0 .321-.062.642-.185.964a3.183 3.183 0 01-.542.908c-.238.284-.6.563-1.088.837zm12.693 6.437v-1.224h-2.622v-9.802h2.622v-1.232h-6.796v1.232h2.621v9.802h-2.621v1.224h6.796zm6.827 0V17.722h3.582v-1.35h-8.71v1.35h3.575v10.907h1.553zm12.07 0v-1.224h-2.622v-9.802h2.621v-1.232h-6.796v1.232h2.622v9.802h-2.622v1.224h6.796zM110.3 29c.707 0 1.507-.171 2.399-.513V27.04c-.83.49-1.65.735-2.46.735-.61 0-1.166-.21-1.669-.628-.502-.419-.915-1.05-1.237-1.892-.323-.842-.485-1.76-.485-2.756 0-.98.157-1.886.47-2.72.312-.835.723-1.469 1.233-1.9.51-.432 1.073-.648 1.688-.648.814 0 1.634.245 2.46.735v-1.454c-.892-.342-1.692-.513-2.399-.513-.943 0-1.807.26-2.59.782-.785.521-1.392 1.287-1.823 2.298-.43 1.011-.646 2.151-.646 3.42 0 1.28.218 2.426.654 3.44.436 1.013 1.045 1.777 1.83 2.29.784.513 1.642.77 2.575.77zm5.228-.371l.807-2.693h4.198l.814 2.693h1.638l-3.698-12.258h-1.576l-3.675 12.258h1.492zm4.659-3.918h-3.483l1.407-4.651.3-1.233.338 1.233 1.438 4.651zm11.17 3.918v-1.35h-4.89V16.37h-1.552V28.63h6.442zm12.516 0l.807-2.693h4.197l.815 2.693h1.638l-3.698-12.258h-1.576l-3.675 12.258h1.492zm4.658-3.918h-3.482l1.407-4.651.3-1.233.338 1.233 1.437 4.651zm11.17 3.918v-1.35h-4.889V16.37h-1.553V28.63h6.443zm9.326 0v-1.35h-4.89v-4.29h4.29v-1.224h-4.29v-4.162h4.767v-1.232h-6.32V28.63h6.443zm4.144 0v-5.205h1.553l3.16 5.205h1.845l-3.452-5.726c.594-.337 1.05-.694 1.364-1.07.315-.377.563-.805.742-1.284.18-.479.27-.955.27-1.43 0-.51-.135-.987-.404-1.429a2.477 2.477 0 00-1.142-.991c-.492-.219-1.258-.328-2.299-.328h-3.19V28.63h1.553zm2.175-6.437h-2.175v-4.589h1.16c1.072 0 1.811.17 2.219.51.407.34.61.796.61 1.37 0 .321-.06.642-.184.964a3.183 3.183 0 01-.542.908c-.238.284-.6.563-1.088.837zm10.071 6.437V17.722H189v-1.35h-8.71v1.35h3.575v10.907h1.552zM4.02 65.509v-3.272h.48c.934 0 1.673-.134 2.218-.402.544-.268.969-.65 1.273-1.147a3.021 3.021 0 00.457-1.607c0-.49-.113-.933-.34-1.33a2.201 2.201 0 00-.977-.904c-.426-.206-1.104-.31-2.033-.31h-2.26v8.972H4.02zm.51-4.168h-.51v-3.902h.71c1.007 0 1.681.15 2.023.451.341.3.512.71.512 1.226 0 .408-.095.788-.287 1.138-.191.351-.464.62-.82.807-.355.187-.898.28-1.628.28zm7.028 4.168v-3.752c.371-.632.752-1.106 1.142-1.422.39-.316.789-.474 1.195-.474.105 0 .25.016.433.047v1.474h1.049v-2.33a4.062 4.062 0 00-1.183-.18c-.54 0-1.028.133-1.467.397-.44.264-.83.662-1.169 1.193v-1.41h-1.09v6.457h1.09zm7.93-8.07c.153 0 .313-.06.48-.179.168-.12.252-.299.252-.537 0-.243-.084-.424-.251-.544-.168-.12-.328-.179-.48-.179-.15 0-.307.06-.475.18-.168.119-.252.3-.252.543 0 .238.084.418.252.537.168.12.326.18.474.18zm2.366 8.07v-.896h-1.821v-5.561h-2.905v.896h1.816v5.56h2.91zm5.148.179c.934 0 1.681-.323 2.244-.968.562-.646.843-1.46.843-2.443 0-.982-.281-1.795-.843-2.439-.563-.643-1.31-.965-2.244-.965-.937 0-1.686.322-2.249.965-.562.644-.843 1.457-.843 2.44 0 .982.281 1.796.843 2.442.563.645 1.312.968 2.25.968zm0-.896c-.585 0-1.05-.224-1.393-.67-.344-.448-.516-1.062-.516-1.845 0-.778.172-1.39.516-1.838.343-.447.808-.67 1.393-.67.582 0 1.045.223 1.389.67.343.447.515 1.06.515 1.838 0 .783-.172 1.397-.515 1.844-.344.447-.807.67-1.389.67zm6.15.717v-3.752c.371-.632.752-1.106 1.142-1.422.39-.316.789-.474 1.195-.474.105 0 .25.016.433.047v1.474h1.049v-2.33a4.062 4.062 0 00-1.183-.18c-.54 0-1.028.133-1.467.397-.44.264-.83.662-1.169 1.193v-1.41h-1.09v6.457h1.09zm7.93-8.07c.152 0 .312-.06.48-.179.168-.12.252-.299.252-.537 0-.243-.084-.424-.252-.544-.168-.12-.328-.179-.48-.179-.148 0-.306.06-.474.18-.168.119-.252.3-.252.543 0 .238.084.418.252.537.168.12.326.18.474.18zm2.366 8.07v-.896h-1.821v-5.561h-2.905v.896h1.815v5.56h2.911zm6.144.179c.215 0 .437-.02.668-.06.23-.041.601-.167 1.112-.38v-.947c-.581.327-1.136.49-1.663.49-.285 0-.532-.05-.74-.15a1.149 1.149 0 01-.502-.47c-.124-.214-.187-.581-.187-1.102v-2.583h2.91v-.896h-2.91v-2.602h-1.09v2.602h-1.633v.896h1.634v2.6c0 .71.096 1.234.29 1.573.193.34.479.595.858.769.378.173.796.26 1.253.26zm3.678 2.514c.765 0 1.39-.208 1.871-.624.482-.416.991-1.258 1.526-2.526l2.536-6h-1.095l-2.015 4.844-2.407-4.844h-1.2l3.021 6.197-.1.248c-.093.25-.253.547-.48.89-.226.343-.476.582-.75.717a2.09 2.09 0 01-.936.202c-.274 0-.56-.046-.861-.138v.948c.261.057.558.086.89.086zm9.716-7.445a.925.925 0 00.674-.277.901.901 0 00.281-.665.894.894 0 00-.284-.668.933.933 0 00-.67-.274.933.933 0 00-.671.274.894.894 0 00-.284.668c0 .258.093.48.28.665a.925.925 0 00.674.277zm0 5.023a.925.925 0 00.674-.277.901.901 0 00.281-.665.894.894 0 00-.284-.667.933.933 0 00-.67-.275.933.933 0 00-.671.275.894.894 0 00-.284.667c0 .258.093.48.28.665a.925.925 0 00.674.277zm12.721-.271v-4.214h3.362v4.214h1.177v-8.971H79.07v3.86h-3.362v-3.86h-1.183v8.97h1.183zm8.563-8.07c.152 0 .312-.06.48-.179.168-.12.252-.299.252-.537 0-.243-.084-.424-.252-.544-.168-.12-.328-.179-.48-.179-.149 0-.307.06-.475.18-.167.119-.251.3-.251.543 0 .238.084.418.251.537.168.12.326.18.475.18zm2.366 8.07v-.896h-1.821v-5.561H81.91v.896h1.815v5.56h2.911zm4.644 2.688c.79 0 1.462-.166 2.018-.498.556-.331.913-.784 1.069-1.358.066-.247.114-.49.143-.731.03-.241.044-.905.044-1.992v-4.566H93.46a10.131 10.131 0 01-.281-.029c-.418-.042-.73-.063-.937-.063-.715 0-1.314.17-1.798.511-.484.341-.87.805-1.16 1.393-.289.588-.433 1.255-.433 2.003 0 .948.217 1.655.653 2.121.435.467.926.7 1.473.7.39 0 .819-.137 1.285-.41.467-.274.866-.68 1.198-1.22v.913c0 .432-.033.778-.1 1.038a1.55 1.55 0 01-.374.685 1.762 1.762 0 01-.72.45 2.83 2.83 0 01-.944.157c-.421 0-.858-.08-1.309-.24a5.062 5.062 0 01-1.139-.552v1.086c.812.401 1.615.602 2.407.602zm-.046-3.538c-.313 0-.591-.163-.835-.488-.244-.326-.366-.809-.366-1.448 0-.883.223-1.58.67-2.093.448-.512 1.007-.769 1.678-.769.332 0 .692.05 1.078.15v2.77c-.324.574-.691 1.03-1.101 1.37-.41.338-.785.508-1.124.508zm6.143.85v-3.763c.285-.54.647-.989 1.087-1.347.439-.359.85-.538 1.233-.538a.84.84 0 01.53.191c.162.127.273.29.333.488.06.199.091.608.091 1.229v3.74h1.09v-3.781c-.004-.62-.021-1.035-.053-1.245a2.311 2.311 0 00-.2-.65c-.12-.267-.334-.493-.64-.68a1.914 1.914 0 00-1.017-.28c-.441 0-.882.14-1.323.422a3.67 3.67 0 00-1.13 1.167V56.18h-1.09v9.33h1.09zM3.137 80.306l.614-1.97H6.95l.62 1.97h1.248L6 71.336H4.8L2 80.305h1.136zm3.548-2.867H4.032l1.072-3.404.228-.902.258.902 1.095 3.404zm4.235 5.376v-2.509c.125.012.218.022.281.03.422.042.734.063.937.063.715 0 1.314-.17 1.798-.512.484-.34.872-.805 1.163-1.393.29-.587.436-1.255.436-2.003 0-.948-.219-1.655-.656-2.121-.437-.466-.93-.7-1.476-.7-.39 0-.818.137-1.283.41-.464.275-.864.681-1.2 1.22v-1.45H9.83v8.965h1.09zm1.09-3.318c-.333 0-.696-.05-1.09-.15v-2.769c.328-.574.696-1.03 1.104-1.37.408-.339.782-.509 1.121-.509.313 0 .592.162.838.486.246.324.369.807.369 1.45 0 .887-.226 1.586-.677 2.096-.45.51-1.006.766-1.666.766zm6.108 3.318v-2.509c.125.012.218.022.28.03.423.042.735.063.938.063.715 0 1.314-.17 1.798-.512.484-.34.872-.805 1.163-1.393.29-.587.436-1.255.436-2.003 0-.948-.219-1.655-.656-2.121-.437-.466-.93-.7-1.476-.7-.39 0-.818.137-1.283.41-.464.275-.864.681-1.2 1.22v-1.45h-1.09v8.965h1.09zm1.09-3.318c-.333 0-.696-.05-1.09-.15v-2.769c.328-.574.696-1.03 1.104-1.37.408-.339.782-.509 1.121-.509.313 0 .592.162.838.486.246.324.369.807.369 1.45 0 .887-.226 1.586-.677 2.096-.45.51-1.006.766-1.666.766zm9.99.81v-.897h-2.043v-8.433h-3.046v.902h1.956v8.427H29.2zm4.686-8.07c.152 0 .313-.06.48-.18.168-.119.252-.298.252-.537 0-.243-.084-.424-.252-.543-.167-.12-.328-.18-.48-.18-.148 0-.306.06-.474.18-.168.12-.252.3-.252.543 0 .24.084.418.252.538.168.12.326.179.474.179zm2.366 8.07v-.897H34.43v-5.56h-2.905v.896h1.815v5.56h2.911zm5.875.179c.66 0 1.354-.122 2.085-.365v-1.034c-.621.208-1.047.328-1.277.361-.23.033-.465.05-.703.05-.789 0-1.368-.265-1.736-.793-.37-.528-.554-1.083-.554-1.665 0-.435.096-.859.287-1.271.191-.413.463-.716.814-.91a2.286 2.286 0 011.125-.293c.6 0 1.282.172 2.044.515v-1.035c-.754-.25-1.468-.375-2.144-.375-.914 0-1.694.323-2.34.97-.646.648-.969 1.461-.969 2.44 0 .651.143 1.232.428 1.743.285.51.682.915 1.191 1.214.51.298 1.093.448 1.749.448zm5.593 0c.39 0 .819-.137 1.285-.41.467-.274.866-.681 1.198-1.22.05.612.12 1.096.205 1.45h1.136c-.164-.485-.246-1.117-.246-1.896v-4.56h-1.095a10.13 10.13 0 01-.281-.03c-.418-.042-.73-.063-.937-.063-.715 0-1.314.17-1.798.512-.484.34-.87.805-1.16 1.393-.289.588-.433 1.255-.433 2.003 0 .948.218 1.655.653 2.121.435.466.926.7 1.473.7zm.258-1.03c-.313 0-.591-.162-.835-.488-.244-.325-.366-.808-.366-1.448 0-.882.223-1.58.67-2.092.448-.513 1.007-.769 1.678-.769.332 0 .692.05 1.078.15v2.769c-.324.574-.691 1.03-1.101 1.37-.41.34-.785.509-1.124.509zm8.814 1.03c.215 0 .437-.02.668-.061.23-.04.6-.167 1.112-.379v-.948c-.581.328-1.136.492-1.663.492-.285 0-.532-.05-.74-.15a1.149 1.149 0 01-.502-.472c-.124-.214-.187-.58-.187-1.1v-2.585h2.91v-.896h-2.91v-2.6h-1.09v2.6h-1.633v.896h1.634v2.601c0 .71.096 1.234.29 1.573.193.339.479.595.858.768.378.174.796.26 1.253.26zm5.886-8.249c.152 0 .312-.06.48-.18.168-.119.252-.298.252-.537 0-.243-.084-.424-.252-.543-.168-.12-.328-.18-.48-.18-.148 0-.307.06-.474.18-.168.12-.252.3-.252.543 0 .24.084.418.252.538.167.12.326.179.474.179zm2.366 8.07v-.897h-1.821v-5.56h-2.905v.896h1.815v5.56h2.911zm5.148.179c.933 0 1.681-.323 2.243-.969.563-.645.844-1.46.844-2.442 0-.983-.281-1.796-.844-2.44-.562-.643-1.31-.964-2.243-.964-.937 0-1.687.321-2.249.965-.562.643-.843 1.456-.843 2.44 0 .982.281 1.796.843 2.441.562.646 1.312.969 2.25.969zm0-.896c-.585 0-1.05-.224-1.394-.67-.343-.448-.515-1.063-.515-1.845 0-.778.172-1.391.515-1.838.344-.447.809-.67 1.394-.67.582 0 1.045.223 1.388.67.344.447.516 1.06.516 1.838 0 .782-.172 1.397-.516 1.844-.343.447-.806.67-1.388.67zm5.593.716v-3.763c.285-.54.648-.988 1.087-1.346.44-.359.85-.538 1.233-.538a.84.84 0 01.53.19c.162.128.273.29.334.49.06.198.09.607.09 1.227v3.74h1.09V76.7c0-.96-.064-1.603-.19-1.93a1.628 1.628 0 00-.665-.795 1.916 1.916 0 00-1.055-.303c-.44 0-.882.14-1.323.421a3.67 3.67 0 00-1.13 1.168v-1.41h-1.09v6.456h1.09zm8.797-4.751a.925.925 0 00.674-.278.901.901 0 00.28-.664.894.894 0 00-.283-.668.933.933 0 00-.67-.274.933.933 0 00-.671.274.894.894 0 00-.284.668c0 .258.093.48.28.664a.925.925 0 00.674.278zm0 5.023a.925.925 0 00.674-.277.901.901 0 00.28-.665.894.894 0 00-.283-.668.933.933 0 00-.67-.274.933.933 0 00-.671.274.894.894 0 00-.284.668c0 .258.093.48.28.665a.925.925 0 00.674.277zm13.014-.272v-3.271h.48c.933 0 1.672-.134 2.217-.402.545-.268.97-.65 1.274-1.147a3.021 3.021 0 00.457-1.607c0-.49-.114-.933-.34-1.33a2.201 2.201 0 00-.978-.904c-.426-.207-1.103-.31-2.032-.31H96.41v8.971h1.183zm.51-4.167h-.51v-3.902h.709c1.007 0 1.681.15 2.023.45.342.301.513.71.513 1.226 0 .409-.096.788-.287 1.139-.192.35-.465.62-.82.806-.356.187-.898.28-1.629.28zm7.197 4.347c.39 0 .82-.137 1.286-.41.467-.274.866-.681 1.198-1.22.05.612.119 1.096.205 1.45h1.136c-.164-.485-.246-1.117-.246-1.896v-4.56h-1.095a10.13 10.13 0 01-.281-.03c-.418-.042-.73-.063-.937-.063-.715 0-1.314.17-1.799.512-.484.34-.87.805-1.16 1.393-.288.588-.433 1.255-.433 2.003 0 .948.218 1.655.653 2.121.436.466.927.7 1.473.7zm.258-1.03c-.312 0-.59-.162-.834-.488-.244-.325-.366-.808-.366-1.448 0-.882.223-1.58.67-2.092.447-.513 1.006-.769 1.678-.769.332 0 .691.05 1.078.15v2.769c-.324.574-.691 1.03-1.101 1.37-.41.34-.785.509-1.125.509zM110.854 83c.765 0 1.389-.208 1.87-.624.483-.416.992-1.258 1.527-2.526l2.536-6h-1.096l-2.014 4.844-2.408-4.844h-1.2l3.022 6.196-.1.249c-.093.25-.253.547-.48.89-.226.343-.476.582-.75.717a2.09 2.09 0 01-.937.202c-.273 0-.56-.046-.86-.139v.948c.261.058.558.087.89.087zm7.49-2.694v-3.728c.207-.536.425-.975.653-1.318.229-.343.443-.514.642-.514.09 0 .191.05.304.15.114.1.17.435.17 1.006v4.404h.914v-3.578c.242-.709.48-1.216.711-1.523.233-.306.45-.46.653-.46a.32.32 0 01.229.105c.07.07.117.17.14.303.024.133.036.38.036.743v4.41h.954v-4.878c0-.702-.1-1.17-.298-1.405-.2-.235-.44-.352-.72-.352-.255 0-.48.072-.674.216-.196.145-.384.34-.566.584-.181.245-.336.564-.465.957v-.347c0-.45-.097-.799-.29-1.043a.926.926 0 00-.764-.367c-.266 0-.528.119-.788.358s-.54.672-.84 1.3v-1.48h-.955v6.457h.954zm9.705.185c.777 0 1.6-.204 2.472-.612v-.983c-.852.462-1.629.694-2.331.694a2.01 2.01 0 01-1.055-.278c-.308-.185-.557-.462-.746-.832a2.65 2.65 0 01-.284-1.226h4.51v-.23c0-1.142-.236-1.985-.706-2.533-.47-.547-1.095-.82-1.872-.82a2.91 2.91 0 00-1.593.453 3.05 3.05 0 00-1.112 1.27 3.825 3.825 0 00-.399 1.698c0 .613.14 1.188.419 1.726.28.537.651.95 1.116 1.24.464.289.991.433 1.581.433zm1.382-4.04h-3.262c.129-.62.354-1.09.677-1.408a1.575 1.575 0 011.145-.477c.437 0 .786.157 1.048.469.261.312.392.732.392 1.26v.156zm3.936 3.855v-3.763c.285-.54.647-.988 1.086-1.346.44-.359.85-.538 1.233-.538a.84.84 0 01.53.19c.162.128.274.29.334.49.06.198.09.607.09 1.227v3.74h1.09V76.7c0-.96-.063-1.603-.19-1.93a1.628 1.628 0 00-.665-.795 1.916 1.916 0 00-1.054-.303c-.441 0-.882.14-1.324.421a3.67 3.67 0 00-1.13 1.168v-1.41h-1.09v6.456h1.09zm9.798.18c.215 0 .438-.02.668-.061.23-.04.601-.167 1.113-.379v-.948c-.582.328-1.136.492-1.664.492-.285 0-.531-.05-.74-.15a1.149 1.149 0 01-.501-.472c-.125-.214-.188-.58-.188-1.1v-2.585h2.911v-.896h-2.91v-2.6h-1.09v2.6h-1.634v.896h1.634v2.601c0 .71.097 1.234.29 1.573.193.339.48.595.858.768.379.174.797.26 1.253.26zm11.626-.18v-5.884l-.023-.93.439.942 3.127 5.872h1.09v-8.97h-1.09v5.97l.03.89h-.03l-.351-.89-3.215-5.97h-1.066v8.97h1.089zm8.973.18c.933 0 1.68-.323 2.243-.969.562-.645.843-1.46.843-2.442 0-.983-.281-1.796-.843-2.44-.563-.643-1.31-.964-2.243-.964-.938 0-1.687.321-2.25.965-.562.643-.843 1.456-.843 2.44 0 .982.281 1.796.844 2.441.562.646 1.311.969 2.249.969zm0-.896c-.586 0-1.05-.224-1.394-.67-.344-.448-.516-1.063-.516-1.845 0-.778.172-1.391.516-1.838.343-.447.808-.67 1.394-.67.581 0 1.044.223 1.388.67.343.447.515 1.06.515 1.838 0 .782-.172 1.397-.515 1.844-.344.447-.807.67-1.388.67zm6.39.896c.39 0 .818-.137 1.285-.41.466-.274.866-.681 1.198-1.22v1.45h1.095v-9.33h-1.095v2.874a10.13 10.13 0 01-.282-.03c-.417-.042-.73-.063-.937-.063-.714 0-1.314.17-1.798.512-.484.34-.87.805-1.16 1.393-.288.588-.433 1.255-.433 2.003 0 .948.218 1.655.653 2.121.436.466.927.7 1.473.7zm.257-1.03c-.312 0-.59-.162-.835-.488-.244-.325-.366-.808-.366-1.448 0-.882.224-1.58.671-2.092.447-.513 1.006-.769 1.678-.769.332 0 .691.05 1.078.15v2.769c-.325.574-.692 1.03-1.102 1.37-.41.34-.784.509-1.124.509zm8.024 1.035c.777 0 1.6-.204 2.471-.612v-.983c-.85.462-1.628.694-2.33.694a2.01 2.01 0 01-1.055-.278c-.308-.185-.557-.462-.747-.832a2.65 2.65 0 01-.284-1.226H181v-.23c0-1.142-.235-1.985-.706-2.533-.47-.547-1.094-.82-1.871-.82a2.91 2.91 0 00-1.593.453 3.05 3.05 0 00-1.113 1.27 3.825 3.825 0 00-.398 1.698c0 .613.14 1.188.419 1.726.279.537.65.95 1.115 1.24.465.289.992.433 1.582.433zm1.382-4.04h-3.262c.129-.62.354-1.09.676-1.408a1.575 1.575 0 011.145-.477c.437 0 .787.157 1.049.469.261.312.392.732.392 1.26v.156z"
                fill="#FFF"
                fillRule="nonzero"
                transform="translate(40 25)"
              />
            </g>
          </svg>
        </div>
      </div>
    </div>
  )
}
export default SubHero
